/**
 * Single source of truth for plan-tier chips across the product.
 *
 * Use this anywhere we want to call attention to a paid feature or
 * indicate the user's current plan. The visual language is:
 *
 *   - PRO     → solid emerald + white text + zap icon (loud, AAA contrast)
 *   - AGENCY  → solid ink + white text + shield icon (loudest tier)
 *   - FREE    → muted gray pill (rarely shown — "free" is the default)
 *
 * Three sizes:
 *   - "sm"  for inline use next to a feature title (default)
 *   - "md"  for paywall headers / hero sections
 *   - "lg"  for big upgrade CTAs
 *
 * Why a single component: before this, every surface drew its own
 * Pro chip — some muted gray, some emerald with a dot, some without
 * a chip at all. The result was a product where "Pro" looked like
 * three different things. Centralising the chip locks the visual
 * vocabulary so every paid surface reads the same.
 */

import type { CSSProperties } from "react"

export type Plan = "free" | "pro" | "agency"
/**
 * `paid` is a tier-or-above marker, not a plan. Use it where a feature
 * is gated behind "any paid plan" (Pro + Agency) so the chip doesn't
 * read as Pro-only — which was confusing visitors when an Agency user
 * saw "PRO" on a feature they actually had access to.
 */
export type PlanBadgePlan = Plan | "paid"
export type PlanBadgeSize = "sm" | "md" | "lg"

const ICON_SIZES: Record<PlanBadgeSize, number> = { sm: 9, md: 11, lg: 13 }

const SIZE_STYLE: Record<PlanBadgeSize, CSSProperties> = {
  sm: { fontSize: 10,    padding: "2px 8px",  letterSpacing: "0.14em", borderRadius: 6,  gap: 4 },
  md: { fontSize: 11.5,  padding: "3px 10px", letterSpacing: "0.16em", borderRadius: 7,  gap: 5 },
  lg: { fontSize: 13,    padding: "5px 12px", letterSpacing: "0.18em", borderRadius: 8,  gap: 6 },
}

interface PlanBadgeProps {
  plan: PlanBadgePlan
  size?: PlanBadgeSize
  /** Optional override label (defaults to the plan name). */
  label?: string
  /** Hide the icon — useful in tight spaces. */
  noIcon?: boolean
  className?: string
}

export function PlanBadge({ plan, size = "sm", label, noIcon, className }: PlanBadgeProps) {
  const display = label ?? plan
  const sz = SIZE_STYLE[size]
  const iconSize = ICON_SIZES[size]

  // "paid" reuses the Pro visual treatment (emerald + zap) — they're
  // the same affordance: "this requires upgrading from free". The
  // only difference is the default label says "Paid" instead of "Pro"
  // so Agency users don't read it as Pro-exclusive.
  if (plan === "paid") {
    return (
      <span
        className={`inline-flex items-center font-extrabold uppercase ${className ?? ""}`}
        style={{
          ...sz,
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          background: "#0F5132",
          color: "#FFFFFF",
          boxShadow: "0 4px 10px -4px rgba(15,81,50,0.55), 0 0 0 1px rgba(15,81,50,0.4) inset",
        }}
      >
        {!noIcon && <ZapIcon size={iconSize} />}
        {label ?? "Paid"}
      </span>
    )
  }

  // Free tier styling — muted, no shadow, no icon. Rarely rendered;
  // included for completeness so callers can pass `plan` directly
  // from a profile without checking it first.
  if (plan === "free") {
    return (
      <span
        className={`inline-flex items-center font-bold uppercase ${className ?? ""}`}
        style={{
          ...sz,
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          background: "rgba(10,10,10,0.06)",
          color: "rgba(10,10,10,0.55)",
          border: "1px solid rgba(10,10,10,0.10)",
        }}
      >
        {display}
      </span>
    )
  }

  // Pro tier — emerald solid, white text, zap.
  if (plan === "pro") {
    return (
      <span
        className={`inline-flex items-center font-extrabold uppercase ${className ?? ""}`}
        style={{
          ...sz,
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          background: "#0F5132",
          color: "#FFFFFF",
          boxShadow: "0 4px 10px -4px rgba(15,81,50,0.55), 0 0 0 1px rgba(15,81,50,0.4) inset",
        }}
      >
        {!noIcon && <ZapIcon size={iconSize} />}
        {display}
      </span>
    )
  }

  // Agency tier — ink solid, white text, shield. Loudest, reserved
  // for the top tier so it visually outranks Pro side-by-side.
  return (
    <span
      className={`inline-flex items-center font-extrabold uppercase ${className ?? ""}`}
      style={{
        ...sz,
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        background: "#0A0A0A",
        color: "#FFFFFF",
        boxShadow: "0 4px 10px -4px rgba(10,10,10,0.55), 0 0 0 1px rgba(10,10,10,0.4) inset",
      }}
    >
      {!noIcon && <ShieldIcon size={iconSize} />}
      {display}
    </span>
  )
}

function ZapIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />
    </svg>
  )
}

function ShieldIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2 4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3z" />
    </svg>
  )
}
